import { FocusEventHandler, ReactNode } from 'react';

import { IconCloseSmall } from '@components/icons/close-small';
import { IconFormIndicatorLocked } from '@components/icons/form-indicator-locked';
import { blockEscapeSelectionClear } from '@components/select/base-ui-event';
import { optionLabelToString } from '@components/select/highlight';
import { useSelectTranslations } from '@components/select/locale';
import { Control, ControlAnchor, DropdownButton, Indicators } from '@components/select/styles';
import { VisuallyHidden } from '@components/visually-hidden';

import { Chip, ChipRemove, Chips, ChipsInput } from './styles';
import { useChipRemovalMark } from './use-chip-removal-mark';

type ChipOption = {
  value: string;
  label: ReactNode;
  filterText?: string;
  color?: string;
};

type ChipsControlProps = {
  inputId: string;
  selected: ChipOption[];
  placeholder: string;
  isOpen: boolean;
  // base-ui doesn't open the list on focus by itself.
  onOpen: () => void;
  hasError: boolean;
  isDisabled?: boolean;
  onBlur?: FocusEventHandler<HTMLInputElement>;
};

export const ChipsControl = ({
  inputId,
  selected,
  placeholder,
  isOpen,
  onOpen,
  hasError,
  isDisabled = false,
  onBlur,
}: ChipsControlProps) => {
  const { t } = useSelectTranslations();
  const removalMark = useChipRemovalMark(selected.map((option) => option.value));
  const lastChip = selected.at(-1);
  const markedChip = removalMark.isLastChipMarked ? lastChip : undefined;

  return (
    <ControlAnchor>
      <Control $hasError={hasError} $isDisabled={isDisabled}>
        <Chips>
          {selected.map((option) => (
            <Chip
              key={option.value}
              data-cy="selectmulti-chip"
              data-pending-removal={option === markedChip || undefined}
              $color={option.color}
            >
              <span>{option.label}</span>
              <ChipRemove
                $color={option.color}
                aria-label={t('remove_option', { label: chipLabel(option) })}
              >
                <IconCloseSmall />
              </ChipRemove>
            </Chip>
          ))}
          <ChipsInput
            id={inputId}
            placeholder={selected.length === 0 ? placeholder : undefined}
            $isCaretHidden={markedChip !== undefined}
            onBlur={(event) => {
              removalMark.clearMark();
              onBlur?.(event);
            }}
            onFocus={onOpen}
            onKeyDown={(event) => {
              blockEscapeSelectionClear(event, isOpen);
              removalMark.handleKeyDown(event);
            }}
          />
        </Chips>
        {isDisabled && (
          <Indicators>
            <DropdownButton aria-hidden>
              <IconFormIndicatorLocked />
            </DropdownButton>
          </Indicators>
        )}
      </Control>
      <VisuallyHidden aria-live="polite" aria-atomic>
        {markedChip ? t('remove_option_confirm', { label: chipLabel(markedChip) }) : ''}
      </VisuallyHidden>
    </ControlAnchor>
  );
};

const chipLabel = (option: ChipOption) => optionLabelToString(option) || option.value;
